import { hash01, distToLine2, mulberry32 } from "./trailGeometry";

/**
 * flowerScatter — the pure wildflower-meadow scatter for FlowerTurf.
 *
 * Split from FlowerTurf (the renderer) so the placement + path-clearance logic
 * can be unit-tested without React/DOM. Flowers are scattered on a jittered grid
 * over the bled-out catalogue area, and one is KEPT only if its whole extent
 * stays outside (W/2 + clearance) of the path centerline — the exact inverse of
 * how the cobbles keep stones INSIDE the path, reusing the same shared geometry
 * + index.
 *
 * Each template is a small cut-paper flower SHAPE — a green leafy base plus a
 * bloom in one of several ARCHETYPES (radial petal ring, bell/tulip cup,
 * pompom, lavender spike, closed bud) — in the same rounded, overlapping-shape
 * art style as the cartoon-tree/shrub SVGs. Templates are hue-agnostic:
 * petal/centre fills route through the --petal-fill/--petal-fold custom
 * properties (FlowerShapes.jsx), and every placed instance picks one of
 * BLOOM_NAMES, whose class maps those properties to theme.css colours
 * (FlowerField.css). A handful of shapes are generated once and placed as
 * lightweight <use> instances; shape and hue are independent per-instance
 * picks, so a few templates yield a varied wildflower field. The archetype MIX
 * is weighted by how many templates each maker bakes (TEMPLATE_MAKERS) — the
 * classic ring stays the majority, the rest sprinkle in.
 */

// ---- tunables --------------------------------------------------------------
const BLEED = 96; // flowers extend this far past .garden-beds on the sides and
//                   bottom; the catalogue clips it
const TOP_BLEED = 64; // shallower on top: the catalogue's clip is held OPEN up
//                       there (Home.css) so these flowers crest the hero seam
//                       and ride over the meadow band — this sets how high they
//                       climb (tuned to roughly half the side bleed's reach)
const FLOWER_CLEAR_FRAC = 0.06; // keep blooms this far beyond the path edge (×W) — a
//                                 slim breathing strip, not a wide bare margin
const FLOWER_REACH = 4; // flowers fade out across this many path-widths (×W) in from
//                         each row's OPEN edge — full at the edge, gone by `reach`
//                         deep — so the games area reads as cleared ground (whether
//                         or not games fill the row) without a hard mowing line. The
//                         open edge alternates with the gutter side — even rows open
//                         right, odd rows open left — matching buildCenterline() in
//                         trailGeometry.
const TEMPLATE_SEED = 0x6a51;
const PETAL_MIN = 5; // petal count is drawn from this small inclusive range, so the
const PETAL_MAX = 8; // meadow mixes simpler and fuller blooms

// Bloom hues, one per CSS bloom class. Each placed instance picks one; the
// class routes --petal-fill/--petal-fold to theme.css colours (FlowerField.css),
// so the hexes live only in the theme.
export const BLOOM_NAMES = ["marigold", "berry", "sky", "butter"];

// Depth layers, painted back -> front (the parallax the old 3 tiles gave). Each
// scatters one flower per `spacing`-px cell, scaled into its size band. `salt`
// gives each layer an independent scatter (uses salt..+6).
export const LAYERS = [
  { name: "back", spacing: 77, scaleLo: 0.5, scaleHi: 0.66, salt: 40 },
  { name: "mid", spacing: 55, scaleLo: 0.7, scaleHi: 0.86, salt: 60 },
  { name: "front", spacing: 40, scaleLo: 0.9, scaleHi: 1.12, salt: 80 },
];

// Compact number: 2 decimals.
const r2 = (v) => Math.round(v * 100) / 100;

// Push 8 points around the circle (cx, cy, r) into the extent list — the
// perimeter samples the path-clearance test walks so a whole bloom stays off
// the trail from every side (blooms are radial, unlike the old upright blades).
function pushRing(pts, cx, cy, r) {
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
}

// The green base every archetype grows from: a woody stem up to -stemH plus a
// jittered leaf arrangement at the ground point (drawn first, under the bloom)
// — a single leaf, the classic pair, or a pair with a smaller third riding up
// the stem — varying size, lean, and which side gets the light green. Also
// pushes each leaf's extent points and the ground contact. Consumes a VARIABLE
// number of rand() calls, so makers must draw any bloom params they want
// independent of the leaf picks BEFORE calling this.
function pushBase(shapes, pts, rand, stemH) {
  shapes.push({ t: "s", d: `M0,0 Q${r2(rand() * 2 - 1)},${r2(-stemH * 0.5)} 0,${r2(-stemH)}`, w: 2.4 });
  pts.push(0, 1);

  const nLeaf = 1 + Math.floor(rand() * 3);
  const firstSide = rand() < 0.5 ? 1 : -1;
  const lightFirst = rand() < 0.5;
  for (let i = 0; i < nLeaf; i++) {
    // Leaves 0/1 are the ground pair (opposite sides); leaf 2 is the smaller
    // one partway up the stem, on whichever side its own roll picks.
    const side = i === 2 ? (rand() < 0.5 ? 1 : -1) : i === 0 ? firstSide : -firstSide;
    const sc = i === 2 ? 0.65 : 1;
    const rx = (5.5 + rand() * 2) * sc;
    const ry = (2.5 + rand() * 1.1) * sc;
    const rot = side * (24 + rand() * 16);
    const cx = side * (3.8 + rand() * 1.6) * sc;
    const cy = i === 2 ? -stemH * (0.35 + rand() * 0.15) : -(2.5 + rand() * 2);
    shapes.push({
      t: "e", cx: r2(cx), cy: r2(cy), rx: r2(rx), ry: r2(ry), rot: r2(rot),
      role: (i % 2 === 0) === lightFirst ? "leafLight" : "leafDark",
    });
    // The leaf's axis-aligned reach (exact rotated-ellipse extents), so the
    // clearance hull tracks whatever arrangement was rolled.
    const th = (rot * Math.PI) / 180;
    const ex = Math.hypot(rx * Math.cos(th), ry * Math.sin(th));
    const ey = Math.hypot(rx * Math.sin(th), ry * Math.cos(th));
    pts.push(r2(cx - ex), r2(cy), r2(cx + ex), r2(cy), r2(cx), r2(cy - ey), r2(cx), r2(cy + ey));
  }
}

// Each maker bakes one template: a hue-agnostic flower shape (green leafy base
// + one bloom archetype). All return the baked draw primitives (`shapes` —
// fills are semantic roles that FlowerShapes maps to CSS custom properties)
// and `pts` — the outer-extent points the path-clearance test uses; the pts
// hull MUST enclose the whole drawn silhouette, since the clearance test never
// looks at the shapes themselves. Origin is the ground-contact point; shapes
// reach up (-y), matching the placement convention (translate, then scale).

// The classic radial ring: petals around a contrasting centre disc.
function makeRingFlower(rand) {
  const shapes = [];
  const pts = [];

  const stemH = 16 + rand() * 7; // bloom centre height above the ground point
  const nPet = PETAL_MIN + Math.floor(rand() * (PETAL_MAX - PETAL_MIN + 1));
  const petRW = 4.4 + rand() * 1.4; // petal ellipse: narrow across
  const petRH = 7.5 + rand() * 2.3; //               long radially
  const petDist = 5 + rand() * 1.6; // centre -> petal-centre
  const centerR = 3.8 + rand() * 1.3;
  const rot0 = rand() * 360; // ring phase, so blooms don't all align
  const Cy = -stemH;

  // Bloom params are all drawn above — pushBase's leaf picks consume a
  // variable number of rand() calls, so drawing them first keeps the bloom
  // stable however the leaves roll.
  pushBase(shapes, pts, rand, stemH);

  // Petal ring as ONE filled path (the petals share a fill, so separate nodes
  // buy nothing and <use> would clone every one). Each petal is an ellipse
  // whose long axis points radially out from the centre, drawn as two
  // elliptical arcs: endpoints at ±rx along the ellipse's own (rotated) x-axis,
  // x-axis-rotation = the petal angle — exactly the rotated <ellipse> it replaces.
  let d = "";
  for (let i = 0; i < nPet; i++) {
    const a = rot0 + (i * 360) / nPet;
    const rad = (a * Math.PI) / 180;
    const px = Math.sin(rad) * petDist;
    const py = Cy - Math.cos(rad) * petDist;
    const ex = Math.cos(rad) * petRW;
    const ey = Math.sin(rad) * petRW;
    const arc = `A${r2(petRW)},${r2(petRH)} ${r2(a)} 1 0 `;
    d +=
      `M${r2(px - ex)},${r2(py - ey)}` +
      arc + `${r2(px + ex)},${r2(py + ey)}` +
      arc + `${r2(px - ex)},${r2(py - ey)}`;
  }
  shapes.push({ t: "p", d, role: "petal" });
  // Centre disc (the darker fold of the same hue) caps the overlapping bases.
  shapes.push({ t: "c", cx: 0, cy: r2(Cy), r: r2(centerR), role: "fold" });

  pushRing(pts, 0, Cy, petDist + petRH);

  return { shapes, pts };
}

// Bell/tulip: a closed cup of three overlapping upright petals — a darker
// back petal peeking out at the top, two lighter front petals splayed over it.
function makeBellFlower(rand) {
  const shapes = [];
  const pts = [];

  const stemH = 17 + rand() * 6;
  const cupW = 5.5 + rand() * 1.5; // cup half-width
  const cupH = 7 + rand() * 2; // petal half-height
  const splay = 15 + rand() * 10; // front petals' outward lean, degrees
  const Cy = -stemH;

  pushBase(shapes, pts, rand, stemH);

  shapes.push({ t: "e", cx: 0, cy: r2(Cy - 1.5), rx: r2(cupW * 0.55), ry: r2(cupH), role: "fold" });
  shapes.push({
    t: "e", cx: r2(-cupW * 0.45), cy: r2(Cy), rx: r2(cupW * 0.5), ry: r2(cupH * 0.92),
    rot: r2(-splay), role: "petal",
  });
  shapes.push({
    t: "e", cx: r2(cupW * 0.45), cy: r2(Cy), rx: r2(cupW * 0.5), ry: r2(cupH * 0.92),
    rot: r2(splay), role: "petal",
  });

  pushRing(pts, 0, Cy, cupH + 2.5);
  return { shapes, pts };
}

// Pompom/allium: a round head of packed bobbles with a few darker ones on top.
function makePompomFlower(rand) {
  const shapes = [];
  const pts = [];

  const stemH = 18 + rand() * 6;
  const headR = 5 + rand() * 1.5; // bobble-centre orbit radius
  const bobR = 2.6 + rand() * 0.8;
  const rot0 = rand() * 360;
  const Cy = -stemH;

  pushBase(shapes, pts, rand, stemH);

  const nBob = 7;
  for (let i = 0; i < nBob; i++) {
    const rad = ((rot0 + (i * 360) / nBob) * Math.PI) / 180;
    shapes.push({
      t: "c", cx: r2(Math.sin(rad) * headR * 0.75), cy: r2(Cy - Math.cos(rad) * headR * 0.75),
      r: r2(bobR + (rand() - 0.5) * 0.6), role: "petal",
    });
  }
  shapes.push({ t: "c", cx: 0, cy: r2(Cy), r: r2(headR * 0.6), role: "petal" }); // fill the middle
  // Three darker freckles, a third of a turn apart (junk jitter on top) so they
  // speckle the head instead of clumping into one blob.
  for (let i = 0; i < 3; i++) {
    const rad = ((rot0 * 1.7 + i * 120 + rand() * 40) * Math.PI) / 180;
    const d = headR * (0.25 + rand() * 0.3);
    shapes.push({
      t: "c", cx: r2(Math.sin(rad) * d), cy: r2(Cy - Math.cos(rad) * d), r: 1.2, role: "fold",
    });
  }

  pushRing(pts, 0, Cy, headR * 0.75 + bobR + 1.5);
  return { shapes, pts };
}

// Lavender/lupine spike: a taller stem with a tapering column of florets
// alternating left/right of it, tipped with a small bud.
function makeSpikeFlower(rand) {
  const shapes = [];
  const pts = [];

  const stemH = 22 + rand() * 6;
  const nLvl = 4 + Math.floor(rand() * 2); // floret pairs, tip down to base
  const lvlGap = 3 + rand() * 0.6;
  const Cy = -stemH;

  pushBase(shapes, pts, rand, stemH);

  shapes.push({ t: "e", cx: 0, cy: r2(Cy - 2), rx: 1.6, ry: 2.4, role: "fold" }); // tip bud
  const spikeLen = (nLvl - 1) * lvlGap + 2;
  for (let i = 0; i < nLvl; i++) {
    const f = i / (nLvl - 1); // 0 at the tip .. 1 at the spike's base
    const y = r2(Cy + 1 + i * lvlGap);
    const off = r2(2 + f * 1.4); // pairs flare out and grow toward the base — the taper
    const rx = r2(2 + f * 0.9);
    const ry = r2(1.5 + f * 0.5);
    const tilt = r2(30 + rand() * 8);
    shapes.push({ t: "e", cx: -off, cy: y, rx, ry, rot: -tilt, role: "petal" });
    shapes.push({ t: "e", cx: off, cy: y, rx, ry, rot: tilt, role: "petal" });
  }

  // The bloom is a tall column, not a disc — one ring can't hug it, so cap
  // both ends and the clearance test samples the whole span.
  pushRing(pts, 0, Cy - 2, 6);
  pushRing(pts, 0, Cy + spikeLen, 7);
  return { shapes, pts };
}

// Closed bud: two tilted petals folded into a teardrop on a short stem — the
// small filler between the showier blooms.
function makeBudFlower(rand) {
  const shapes = [];
  const pts = [];

  const stemH = 10 + rand() * 4;
  const budR = 2.6 + rand() * 0.5; // petal half-width
  const budH = 4.2 + rand() * 0.8; // petal half-height
  const Cy = -stemH;

  pushBase(shapes, pts, rand, stemH);

  const By = Cy - budH * 0.5; // bud centre, riding just above the stem top
  shapes.push({ t: "e", cx: -1.1, cy: r2(By), rx: r2(budR), ry: r2(budH), rot: -14, role: "petal" });
  shapes.push({ t: "e", cx: 1.1, cy: r2(By), rx: r2(budR), ry: r2(budH), rot: 14, role: "petal" });
  shapes.push({ t: "c", cx: 0, cy: r2(Cy + 1), r: 1.6, role: "fold" }); // calyx cap

  pushRing(pts, 0, By, budH + 2.5);
  return { shapes, pts };
}

// The baked template list IS the archetype mix: a uniform per-instance pick
// over it (sampleCell) makes each archetype as common as its share of makers —
// the classic ring stays the majority, the newer shapes sprinkle in. Ring
// entries come first so their seeds (TEMPLATE_SEED + i*97) predate the split
// and the existing meadow doesn't reshuffle.
const TEMPLATE_MAKERS = [
  makeRingFlower, makeRingFlower, makeRingFlower, makeRingFlower,
  makeBellFlower, makePompomFlower, makeSpikeFlower, makeBudFlower,
];

// The templates are deterministic (fixed seed, no inputs), so build once and
// share: callers re-run per resize tick / geometry change, and stable shape
// identities keep the memoized renderers effective.
let templatesCache = null;
export function makeTemplates() {
  if (!templatesCache) {
    templatesCache = TEMPLATE_MAKERS.map((make, i) => make(mulberry32(TEMPLATE_SEED + i * 97)));
  }
  return templatesCache;
}

// One scatter sample for grid cell (ix, iy) of `layer`: the jittered position
// (in grid space — callers add their own origin offset) plus the per-instance
// picks. Owns the hash01 salt streams +0/+1 (jitter), +2 (shape template),
// +3 (scale), +4 (flip), +6 (bloom hue); the keep-roll stream (+5) is left to
// each builder, whose keep predicates differ (path clearance here, edge bands
// in edgeFlowers.js).
export function sampleCell(ix, iy, layer, nTemplates, nBlooms) {
  const sp = layer.spacing;
  return {
    jx: (ix + 0.15 + 0.7 * hash01(ix, iy, layer.salt)) * sp,
    jy: (iy + 0.15 + 0.7 * hash01(ix, iy, layer.salt + 1)) * sp,
    ti: (hash01(ix, iy, layer.salt + 2) * nTemplates) | 0,
    bi: (hash01(ix, iy, layer.salt + 6) * nBlooms) | 0,
    s: layer.scaleLo + hash01(ix, iy, layer.salt + 3) * (layer.scaleHi - layer.scaleLo),
    flip: hash01(ix, iy, layer.salt + 4) < 0.5 ? 1 : -1,
  };
}

// Scatter the three layers over the bled-out catalogue area, dropping any flower
// whose extent comes within (W/2 + clearance) of the path. Returns the viewBox
// box, the flower templates (baked shapes + extent pts), and per-layer instances.
export function buildFlowerField({ line, index, W, width, edge = 0, rows }) {
  const bottom = rows[rows.length - 1].top + rows[rows.length - 1].height;
  const x0 = -BLEED;
  const y0 = -TOP_BLEED;
  const vw = width + 2 * BLEED;
  const vh = bottom + TOP_BLEED + BLEED;
  const rejR = W / 2 + W * FLOWER_CLEAR_FRAC;
  const rejR2 = rejR * rejR;
  const reach = W * FLOWER_REACH; // depth of the fade band, in from the open edge

  // Probability a flower at (x, y) is CLEARED for the games area: 0 right at a
  // row's OPEN edge, ramping (√depth) to 1 by `reach` deep, and staying 1 further
  // in — so flowers thin out across the band rather than stopping at a hard line,
  // leaving the games' ground bare. The √ keeps more flowers near the open edge
  // and thins faster as it goes deep. Bleed above the first / below the last row,
  // and the meadow margins outside the U, return 0 (their flowers are untouched).
  // The interior span is [edge, width - edge]; even rows open RIGHT, odd rows open
  // LEFT (the gutter side flips each row — see buildCenterline in trailGeometry).
  const gamesAreaClearProb = (x, y) => {
    if (y < rows[0].top || y >= bottom) return 0; // bleed strips: leave flowers
    let i = rows.length - 1;
    for (let r = 0; r < rows.length; r++) {
      if (y < rows[r].top + rows[r].height) { i = r; break; }
    }
    let depth;
    if (i % 2 === 0) {
      if (x < edge) return 0; // left meadow margin
      depth = width - edge - x; // in from the right (open) edge
    } else {
      if (x > width - edge) return 0; // right meadow margin
      depth = x - edge; // in from the left (open) edge
    }
    if (depth <= 0) return 0; // at / outside the open edge
    return depth >= reach ? 1 : Math.sqrt(depth / reach);
  };

  const templates = makeTemplates();
  const layers = LAYERS.map((layer) => {
    const sp = layer.spacing;
    const cols = Math.ceil(vw / sp);
    const lrows = Math.ceil(vh / sp);
    const uses = [];
    for (let ix = 0; ix < cols; ix++) {
      for (let iy = 0; iy < lrows; iy++) {
        const c = sampleCell(ix, iy, layer, templates.length, BLOOM_NAMES.length);
        const jx = x0 + c.jx;
        const jy = y0 + c.jy;

        // Thin the games area first (a cheap base-point test): clear with a
        // probability that ramps from 0 at the open edge to 1 by `reach` deep, so
        // flowers fade out instead of cutting off. A per-cell hash (salt + 5,
        // independent of the sampler's streams) keeps the choice stable.
        if (hash01(ix, iy, layer.salt + 5) < gamesAreaClearProb(jx, jy)) continue;

        // Keep the flower only if every extent point clears rejR of the path. We
        // test ALL of them — no base-distance early-out: blooms reach ~19–43px
        // from the ground point (stem + bloom, archetype-dependent, ×1.12 at
        // front scale), comparable to the path width, while the shared index is only
        // exact within W of the path, so a "far" base can't certify a far petal.
        // The test itself is exact, because rejR < W puts its threshold in the
        // exact band. It's cheap regardless: a point off the path hits empty
        // index cells.
        const sx = c.s * c.flip;
        const p = templates[c.ti].pts;
        let clash = false;
        for (let q = 0; q < p.length; q += 2) {
          if (distToLine2(jx + p[q] * sx, jy + p[q + 1] * c.s, line, index) <= rejR2) {
            clash = true;
            break;
          }
        }
        if (clash) continue;
        uses.push({ x: jx, y: jy, sx, sy: c.s, ti: c.ti, bi: c.bi });
      }
    }
    return uses;
  });

  return { x0, y0, vw, vh, templates, layers };
}
